import { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import numbro from 'numbro';

import { StaminaType } from '../../../../constants/currencies';
import { getColorfulGameBGByIndex } from '../../../../constants/Games/gameBackground';
import { SVGIconType } from '../../../../constants/svgIcon';
import { Profile } from '../../../../models/UserStore';
import {
  AssetDisplayBar,
  CurrencyAmount,
  GameActionButton,
  GameActionButtonVariant,
  LoadingSpinner,
  PngIcon,
  SvgIcon,
} from '../../../Molecules';
import {
  GameStartInfoBoard,
  GameStartInfoBoardProps,
  GameStartVSBoard,
  GameStartVSBoardProps,
  MemoryHelperPanel,
  MemoryHelperPanelProps,
} from '../../../Organisms';
import { AnimatedNumber, AnimatedNumberProps } from '../GameEndTemplate';

import styles from './GameStartTemplate.module.css';

type GameStartTemplateProps = {
  profile: Profile | undefined;
  currencies: CurrencyAmount[];
  isAssistedGame: boolean;
  gameFee: number | undefined;
  gameBGIndex: number | undefined;
  gameStartInfoBoardProps: GameStartInfoBoardProps;
  gameStartVSBoardProps: GameStartVSBoardProps;
  memoryHelperProps: MemoryHelperPanelProps;
  isLoading: boolean;
  onClickStart: () => void;
  onClickHeadphonesSuggested?: () => void;
  onClickExit: () => void;
  onClickShare?: () => void;
  onClickMemoryHelper: () => void;
  staminaAnimatedNumberProps?: AnimatedNumberProps;
  tokenAnimatedNumberProps?: AnimatedNumberProps;
  isShowAssetBar?: boolean;
};

const GameStartTemplate = memo(
  ({
    profile,
    currencies,
    isAssistedGame,
    gameFee,
    gameBGIndex,
    gameStartInfoBoardProps,
    gameStartVSBoardProps,
    memoryHelperProps,
    isLoading,
    onClickStart,
    onClickHeadphonesSuggested,
    onClickExit,
    // onClickShare,
    onClickMemoryHelper,
    onClickShare,
    staminaAnimatedNumberProps,
    tokenAnimatedNumberProps,
    isShowAssetBar,
  }: GameStartTemplateProps) => {
    const { t } = useTranslation();
    const gameBG =
      gameBGIndex !== undefined
        ? getColorfulGameBGByIndex(gameBGIndex)
        : undefined;

    // const audioPlayer = useGameControllerStore.getState().audioPlayer;
    const isGameStaminaZero = currencies.some(
      (currency) =>
        currency.type === StaminaType.GameStamina && currency.amount === 0,
    );

    useEffect(() => {
      document.documentElement.style.scrollBehavior = 'auto';
      document.body.style.scrollBehavior = 'auto';
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, []);
    return (
      <div
        className={styles.backgroundImage}
        style={
          gameBG
            ? {
                backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0.7)),
                  url(${gameBG})`,
              }
            : undefined
        }
      >
        <div className={styles.gameStartTemplate}>
          {isLoading && <LoadingSpinner loading={isLoading} />}
          <div className={styles.pageHeader}>
            <motion.div
              whileTap={{ opacity: 0.8 }}
              className={styles.backIcon}
              onClick={onClickExit}
            >
              <SvgIcon name={SVGIconType.CircleBack} width={44} height={44} />
            </motion.div>
            {profile && currencies.length > 0 && isShowAssetBar ? (
              <div style={{ position: 'relative' }} className={styles.slideIn}>
                <AssetDisplayBar profile={profile} currencies={currencies} />
                {staminaAnimatedNumberProps ? (
                  <AnimatedNumber {...staminaAnimatedNumberProps} />
                ) : null}
                {tokenAnimatedNumberProps ? (
                  <AnimatedNumber {...tokenAnimatedNumberProps} />
                ) : null}
              </div>
            ) : null}
          </div>
          <div className={styles.content}>
            <GameStartInfoBoard {...gameStartInfoBoardProps} />

            {isAssistedGame && memoryHelperProps.onClickMemoryHelper ? (
              <MemoryHelperPanel {...memoryHelperProps} />
            ) : (
              <GameStartVSBoard {...gameStartVSBoardProps} />
            )}

            <div className={styles.buttonGroup}>
              {/* {onClickShare && (
                <div className={styles.shareButton} onClick={onClickShare}>
                  <SvgIcon
                    name={SVGIconType.Share}
                    width={24}
                    height={24}
                    color="white"
                  />
                  <p className={styles.shareText}>{t('Share')}</p>
                </div>
              )} */}

              {isAssistedGame ? (
                <GameActionButton
                  variant={GameActionButtonVariant.Orange}
                  onClick={onClickStart}
                  leading={
                    <SvgIcon
                      name={SVGIconType.TutorGames}
                      width={24}
                      height={24}
                      color="white"
                    />
                  }
                  trailing={
                    <SvgIcon
                      name={SVGIconType.Play}
                      width={24}
                      height={24}
                      color="white"
                    />
                  }
                >
                  <p style={{ padding: '0 1rem' }}>{t('Learn')}</p>
                </GameActionButton>
              ) : (
                <GameActionButton
                  variant={GameActionButtonVariant.Green}
                  onClick={onClickStart}
                  leading={
                    <SvgIcon
                      name={SVGIconType.PracticeTick}
                      width={24}
                      height={24}
                      color="white"
                    />
                  }
                  trailing={
                    <>
                      {gameFee ? (
                        <div className={styles.currencyItem}>
                          <PngIcon
                            name={!isGameStaminaZero ? 'GameStamina' : `Token`}
                            alt={`Token`}
                            width={22}
                            height={22}
                            className={styles.icon}
                          />
                          <span>
                            {numbro(gameFee).format({
                              thousandSeparated: true,
                            })}
                          </span>
                        </div>
                      ) : null}
                      <SvgIcon
                        name={SVGIconType.Play}
                        width={24}
                        height={24}
                        color="white"
                      />
                    </>
                  }
                >
                  <p style={{ padding: '0 1rem' }}>{t('Start')}</p>
                </GameActionButton>
              )}

              <div
                className={styles.headphoneSuggest}
                onClick={onClickHeadphonesSuggested}
              >
                <SvgIcon
                  name={SVGIconType.Headphone}
                  width={24}
                  height={24}
                  color={'#A9D0CE'}
                />
                <div>
                  <p className={styles.headphoneSuggestCaption}>
                    {t('Headphones suggested')}
                  </p>
                  <p className={styles.headphoneSuggestCaption}>
                    {t('(to achieve 3x effectiveness!)')}
                  </p>
                </div>
              </div>

              {!isAssistedGame && (
                <div
                  className={styles.memoryHelperButton}
                  onClick={onClickMemoryHelper}
                >
                  <SvgIcon
                    name={SVGIconType.MemoryHelper}
                    width={24}
                    height={24}
                  />
                  <div className={styles.memoryHelperText}>
                    {t('Memory Helper')}
                  </div>
                  <SvgIcon name={SVGIconType.Arrow} width={16} height={16} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  },
);

export default GameStartTemplate;
